import { useEffect, useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import OnboardingProgressHeader from '../components/OnboardingProgressHeader';
import PrimaryButton from '../components/PrimaryButton';
import rabbiKidImage from '../assets/rabbi-kid-2.png';
import xCircleImage from '../assets/x-circle.png';
import { findRabbiByCode } from '../utils/rabbiesFirestore';
import { RabbiProfile } from './ChooseRabbiPage';
import { setSelectedRabbiId, setTradition, setRabbiConnected } from '../utils/userSelections';
import {
  trackPageView,
  trackClick,
  trackNavigation,
  trackTraditionSelection,
} from '../utils/analytics';

const NAVY = '#13294B';
const BORDER = '#D1D5DB';

type CodeStatus = 'idle' | 'loading' | 'found' | 'not_found';

const ConnectRabbiPage: FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dir = i18n.dir(i18n.language);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<CodeStatus>('idle');
  const [foundRabbi, setFoundRabbi] = useState<RabbiProfile | null>(null);

  useEffect(() => {
    trackPageView('ConnectRabbi');
  }, []);

  const handleSend = async (): Promise<void> => {
    const trimmed = code.trim();
    if (!trimmed || status === 'loading') return;
    trackClick('Send Connection Code', 'ConnectRabbi');
    setStatus('loading');
    try {
      const rabbi = await findRabbiByCode(trimmed);
      if (rabbi) {
        setFoundRabbi(rabbi);
        setStatus('found');
      } else {
        setFoundRabbi(null);
        setStatus('not_found');
      }
    } catch (e) {
      console.error('Failed to look up rabbi by code:', e);
      setFoundRabbi(null);
      setStatus('not_found');
    }
  };

  const handleClear = (): void => {
    setStatus('idle');
    setFoundRabbi(null);
    setCode('');
  };

  const handleSkip = (): void => {
    trackClick('Skip Connect Rabbi', 'ConnectRabbi');
    trackNavigation('ConnectRabbi', 'UserPortal', 'button');
    navigate('/user-portal');
  };

  const handleContinue = (): void => {
    if (!foundRabbi) return;
    trackClick('Continue', 'ConnectRabbi');
    setSelectedRabbiId(foundRabbi.id);
    setRabbiConnected(true);
    const rabbiTradition = foundRabbi.tradition.toLowerCase();
    setTradition({ key: rabbiTradition, length: 'long' });
    trackTraditionSelection(rabbiTradition, 'long');
    trackNavigation('ConnectRabbi', 'Tradition', 'button');
    navigate('/tradition');
  };

  const foundRabbiName = foundRabbi
    ? i18n.language === 'he'
      ? foundRabbi.nameHe
      : foundRabbi.nameEn
    : '';

  return (
    <div
      className="mx-auto w-full max-w-[980px] flex flex-col"
      style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      <OnboardingProgressHeader step={3} />
      <main className="flex-1 overflow-y-auto px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+80px)]">
        <h2 className="text-center text-lg font-bold mb-1" style={{ color: NAVY }}>
          {t('connect_rabbi_title')}
        </h2>
        <p className="text-center text-sm mb-2 mx-auto" style={{ color: '#3F4756', maxWidth: 320 }}>
          {t('connect_rabbi_subtitle')}
        </p>

        <div className="flex justify-center mb-5">
          <img
            src={rabbiKidImage}
            alt=""
            className="h-[170px] object-contain"
            draggable={false}
            style={{
              maskImage: 'linear-gradient(to bottom, black 65%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 65%, transparent 100%)',
            }}
          />
        </div>

        <div className="mx-auto" style={{ maxWidth: 320 }}>
          {status === 'found' ? (
            <div
              dir={dir}
              className="flex items-center justify-between gap-2 rounded-full px-3 py-2 mb-3"
              style={{ background: '#f0fdf4' }}
            >
              <button
                type="button"
                onClick={handleClear}
                aria-label={t('coupon_remove') || 'Clear'}
                className="flex-shrink-0 flex items-center justify-center cursor-pointer"
                style={{
                  width: 28,
                  height: 28,
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                }}
              >
                <img
                  src={xCircleImage}
                  alt=""
                  className="w-full h-full object-contain"
                  draggable={false}
                />
              </button>
              <span
                className="flex-1 text-center rounded-full py-1.5 text-sm font-bold"
                style={{ background: '#dcfce7', color: '#166534' }}
              >
                {t('connect_rabbi_found_title', { name: foundRabbiName })}
              </span>
              <span
                className="flex-shrink-0 flex items-center justify-center rounded-full"
                style={{ width: 22, height: 22, background: '#22c55e' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M7 12l4 4 6-6"
                    stroke="white"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          ) : (
            <>
              <div dir={dir} className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={code}
                  maxLength={5}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void handleSend()}
                  placeholder={t('connect_rabbi_code_placeholder') || ''}
                  className="kol-input flex-1"
                  style={{
                    borderRadius: 18,
                    fontSize: 14,
                    border: `1px solid ${BORDER}`,
                    padding: '12px 16px',
                    textAlign: dir === 'rtl' ? 'right' : 'left',
                  }}
                  aria-label={t('connect_rabbi_code_placeholder') || ''}
                />
                <button
                  type="button"
                  onClick={() => void handleSend()}
                  disabled={!code.trim() || status === 'loading'}
                  className="flex-shrink-0"
                  style={{
                    borderRadius: 9999,
                    padding: '12px 20px',
                    fontWeight: 700,
                    fontSize: 14,
                    border: 'none',
                    background: '#E9C546',
                    color: '#132A49',
                    cursor: !code.trim() || status === 'loading' ? 'not-allowed' : 'pointer',
                    opacity: !code.trim() || status === 'loading' ? 0.6 : 1,
                  }}
                >
                  {status === 'loading' ? '…' : t('connect_rabbi_send')}
                </button>
              </div>

              {status === 'not_found' && (
                <p className="text-center text-sm mb-2" style={{ color: '#991B1B' }}>
                  {t('connect_rabbi_not_found')}
                </p>
              )}
            </>
          )}

          {status === 'found' ? (
            <PrimaryButton
              onClick={handleContinue}
              text={t('connect_rabbi_continue')}
              variant="gold"
              style={{ fontWeight: 700, fontSize: 18, marginTop: 12 }}
            />
          ) : (
            <PrimaryButton
              onClick={handleSkip}
              text={t('connect_rabbi_skip')}
              variant="gold"
              style={{ fontWeight: 700, fontSize: 18, marginTop: 12 }}
            />
          )}
          <p className="text-center text-sm mt-2 mb-0" style={{ color: '#3F4756' }}>
            {t('connect_rabbi_change_later')}
          </p>
        </div>
      </main>
      <BottomNav />
    </div>
  );
};

export default ConnectRabbiPage;
